import type {
  TagType,
  TagRequest,
  TagResponse,
  ThematicRequest,
  EconomicSectorRequest,
  GrammaticalReferenceRequest,
  ApiResult,
  LanguageCode,
} from '@/types/api';
import { getAuthToken, handleAuthError } from '@/stores/authStore';

// Configuration from environment
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';
const REQUEST_TIMEOUT = parseInt(import.meta.env.VITE_REQUEST_TIMEOUT || '30000', 10);

// Header constants
const TRACE_ID_HEADER = 'x-request-id';

// Auth error flag to track if we need to redirect
let authErrorCallback: ((reason: string) => void) | null = null;

/**
 * Set the callback to be called when an auth error occurs
 * This allows the router to handle the redirect
 */
export function setAuthErrorCallback(callback: (reason: string) => void): void {
  authErrorCallback = callback;
}

/**
 * Build the request payload based on type - STRICT per contract
 * - thematic: lesson_markdown only
 * - economic_sector: lesson_markdown + valid_values
 * - grammatical_reference: lesson_markdown + language + valid_values
 */
export function buildRequestPayload(
  type: TagType,
  lessonMarkdown: string,
  validValues?: string[],
  language?: LanguageCode
): TagRequest {
  switch (type) {
    case 'thematic':
      // STRICT: Only lesson_markdown, no valid_values, no language
      return {
        lesson_markdown: lessonMarkdown,
      } as ThematicRequest;

    case 'economic_sector':
      // STRICT: lesson_markdown + valid_values (required)
      return {
        lesson_markdown: lessonMarkdown,
        valid_values: validValues || [],
      } as EconomicSectorRequest;

    case 'grammatical_reference':
      // STRICT: lesson_markdown + language + valid_values (both required)
      return {
        lesson_markdown: lessonMarkdown,
        language: language || 'en',
        valid_values: validValues || [],
      } as GrammaticalReferenceRequest;

    default:
      throw new Error(`Unknown tag type: ${type}`);
  }
}

/**
 * Extract response headers as a plain object
 */
function extractHeaders(headers: Headers): Record<string, string> {
  const result: Record<string, string> = {};
  headers.forEach((value, key) => {
    result[key.toLowerCase()] = value;
  });
  return result;
}

/**
 * Handle authentication errors (401/403)
 */
function handleUnauthorizedResponse(status: number, currentPath: string): void {
  const reason = status === 401 ? 'unauthorized' : 'forbidden';
  handleAuthError(currentPath);
  if (authErrorCallback) {
    authErrorCallback(reason);
  }
}

/**
 * Call the tags extraction endpoint
 */
export async function callTagsEndpoint(
  type: TagType,
  payload: TagRequest
): Promise<ApiResult<TagResponse>> {
  const url = `${API_BASE_URL}/api/v1/tags?type=${type}`;
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT);

  try {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    };

    // Add JWT token if available
    const token = getAuthToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    // Handle authentication errors
    if (response.status === 401 || response.status === 403) {
      // Get current path for redirect after login
      const currentPath = window.location.pathname + window.location.search;
      handleUnauthorizedResponse(response.status, currentPath);
      
      return {
        ok: false,
        error: response.status === 401 ? 'Unauthorized' : 'Forbidden',
      };
    }

    const responseHeaders = extractHeaders(response.headers);
    const traceId = responseHeaders[TRACE_ID_HEADER] || '';

    // Try to parse response body
    let data: TagResponse | undefined;
    let errorMessage: string | undefined;

    const contentType = response.headers.get('content-type');
    if (contentType?.includes('application/json')) {
      try {
        const json = await response.json();
        if (response.ok) {
          data = json as TagResponse;
        } else {
          // Error response - extract message
          errorMessage = json.detail || json.message || json.title || 'Request failed';
          if (typeof errorMessage === 'object') {
            errorMessage = JSON.stringify(errorMessage);
          }
        }
      } catch {
        errorMessage = 'Failed to parse response JSON';
      }
    } else {
      const text = await response.text();
      if (!response.ok) {
        errorMessage = text || `HTTP ${response.status}`;
      }
    }

    if (response.ok && data) {
      return {
        ok: true,
        data,
        traceId,
        responseHeaders,
      };
    }

    return {
      ok: false,
      error: errorMessage || `HTTP ${response.status}`,
      traceId,
      responseHeaders,
    };
  } catch (error) {
    clearTimeout(timeoutId);

    if (error instanceof Error) {
      if (error.name === 'AbortError') {
        return {
          ok: false,
          error: 'Request timed out',
        };
      }
      return {
        ok: false,
        error: error.message || 'Network error',
      };
    }

    return {
      ok: false,
      error: 'Unknown error occurred',
    };
  }
}

/**
 * Execute tag extraction with full payload construction
 */
export async function executeTagExtraction(
  type: TagType,
  lessonMarkdown: string,
  validValues?: string[],
  language?: LanguageCode
): Promise<ApiResult<TagResponse> & { requestPayload: TagRequest }> {
  const payload = buildRequestPayload(type, lessonMarkdown, validValues, language);
  const result = await callTagsEndpoint(type, payload);

  return {
    ...result,
    requestPayload: payload,
  };
}
